/**
 * Admin V3 — Operations / Notifications — top-level page
 *
 * Mounted at: /admin-v3/operations/notifications (see AdminV3.js)
 *
 * GOVERNANCE MEANING: Unified notification delivery log (SMS + Email, read-only).
 * Mirror of legacy NotificationsPanel (AdminControlTower.js:815-963), reading
 * the unified `notification_logs` collection (Twilio + Resend).
 *
 * SCOPE FOR PHASE 2.7:
 *   - GET /admin/control-tower/notifications/sms   (channel=sms or all)
 *   - GET /admin/control-tower/notifications/email (channel=email or all)
 *   - Channel filter: all / sms / email (when "all", both endpoints fire in
 *     parallel via the existing useAdminQuery `enabled` option and the
 *     results are merged client-side, sorted by timestamp DESC).
 *   - Server-side status filter (?status=pending|sent|failed|skipped)
 *   - Client-side search over loaded page (recipient or booking_reference)
 *   - Per-row expansion with provider, provider_id, message_type, error_message
 *   - Summary line: total · sent · failed (summed across active channels)
 *
 * MUTATIONS:
 *   - Resend (AV3 A-5): FAILED rows expose a resend control (POST
 *     /admin/control-tower/notifications/resend), RBAC-gated and F14-honesty-
 *     gated — disabled with an explicit reason until the backend actually
 *     re-delivers (Category B / F14). No broadcast surface (no backend endpoint).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Bell } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import OperationsNotificationsSearchBar from './OperationsNotificationsSearchBar';
import OperationsNotificationsTable from './OperationsNotificationsTable';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 100;

const OperationsNotificationsPage = () => {
  const locale = DEFAULT_LOCALE;

  const [channel, setChannel] = useState('all');     // 'all' | 'sms' | 'email'
  const [status, setStatus] = useState('');          // '' = all statuses
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [search]);

  // Compose query params (status applies to both endpoints when set)
  const queryParams = { limit: DEFAULT_LIMIT };
  if (status) queryParams.status = status;

  const enableSms   = channel === 'all' || channel === 'sms';
  const enableEmail = channel === 'all' || channel === 'email';

  const smsQuery   = useAdminQuery('/admin/control-tower/notifications/sms',   queryParams, { enabled: enableSms });
  const emailQuery = useAdminQuery('/admin/control-tower/notifications/email', queryParams, { enabled: enableEmail });

  // Merge logs from active endpoints, sorted DESC by timestamp.
  const allLogs = useMemo(() => {
    const smsLogs   = enableSms   && Array.isArray(smsQuery.data?.logs)   ? smsQuery.data.logs   : [];
    const emailLogs = enableEmail && Array.isArray(emailQuery.data?.logs) ? emailQuery.data.logs : [];
    const merged = [...smsLogs, ...emailLogs];
    merged.sort((a, b) => {
      const ta = a?.timestamp || a?.sent_at || '';
      const tb = b?.timestamp || b?.sent_at || '';
      return ta < tb ? 1 : ta > tb ? -1 : 0;
    });
    return merged;
  }, [enableSms, enableEmail, smsQuery.data, emailQuery.data]);

  // Client-side search across recipient + booking_reference (case-insensitive)
  const filteredLogs = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return allLogs;
    return allLogs.filter((l) =>
      (l.recipient || '').toLowerCase().includes(q) ||
      (l.booking_reference || '').toLowerCase().includes(q)
    );
  }, [allLogs, debouncedSearch]);

  // Summary aggregation across active channels
  const summary = useMemo(() => {
    let total = 0, sent = 0, failed = 0;
    if (enableSms && smsQuery.data) {
      total  += smsQuery.data.total ?? 0;
      sent   += smsQuery.data.stats?.sent ?? 0;
      failed += smsQuery.data.stats?.failed ?? 0;
    }
    if (enableEmail && emailQuery.data) {
      total  += emailQuery.data.total ?? 0;
      sent   += emailQuery.data.stats?.sent ?? 0;
      failed += emailQuery.data.stats?.failed ?? 0;
    }
    return { total, sent, failed };
  }, [enableSms, enableEmail, smsQuery.data, emailQuery.data]);

  const isFilterActive = Boolean(debouncedSearch) || Boolean(status) || channel !== 'all';
  const loading = (enableSms && smsQuery.loading) || (enableEmail && emailQuery.loading);
  const error = smsQuery.error || emailQuery.error || null;
  const dataLoaded = (enableSms && smsQuery.data) || (enableEmail && emailQuery.data);

  // Show 100-cap hint when EITHER channel hit its per-channel cap
  const smsAtCap   = enableSms   && smsQuery.data?.logs?.length === DEFAULT_LIMIT;
  const emailAtCap = enableEmail && emailQuery.data?.logs?.length === DEFAULT_LIMIT;
  const isAtCap = smsAtCap || emailAtCap;

  const onRetry = () => {
    if (enableSms) smsQuery.refetch();
    if (enableEmail) emailQuery.refetch();
  };

  const showSkeleton = loading && !dataLoaded;

  return (
    <SectionCard
      title={t('operations.notifications.title', locale)}
      icon={Bell}
      iconClass="bg-violet-100 text-violet-600"
    >
      <div className="space-y-4">
        <OperationsNotificationsSearchBar
          channel={channel}
          onChannelChange={setChannel}
          status={status}
          onStatusChange={setStatus}
          search={search}
          onSearchChange={setSearch}
          resultsCount={filteredLogs.length}
          isFilterActive={isFilterActive}
          loading={loading}
          locale={locale}
        />

        {/* Summary line (sums across active channels) */}
        {dataLoaded && (
          <div
            className="text-[12px] text-slate-500 flex flex-wrap gap-x-3 gap-y-1"
            data-testid="adminv3-operations-notifications-summary"
          >
            <span>{t('operations.notifications.summary.total',  locale).replace('{n}', String(summary.total))}</span>
            <span className="text-slate-300">·</span>
            <span>{t('operations.notifications.summary.sent',   locale).replace('{n}', String(summary.sent))}</span>
            <span className="text-slate-300">·</span>
            <span className={summary.failed > 0 ? 'text-rose-600 font-medium' : ''}>
              {t('operations.notifications.summary.failed', locale).replace('{n}', String(summary.failed))}
            </span>
          </div>
        )}

        <OperationsNotificationsTable
          logs={filteredLogs}
          loading={showSkeleton}
          error={error}
          isFilterActive={isFilterActive}
          onRetry={onRetry}
          locale={locale}
        />

        {/* Per-channel 100-cap governance hint */}
        {isAtCap && !loading && (
          <p
            className="text-[12px] text-slate-400 italic"
            data-testid="adminv3-operations-notifications-cap-hint"
          >
            {t('operations.notifications.cap.hint', locale).replace('{n}', String(DEFAULT_LIMIT))}
          </p>
        )}
      </div>
    </SectionCard>
  );
};

export default OperationsNotificationsPage;
